import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Alert, Button, Card, CardContent, CardHeader, Checkbox, FormControlLabel, Grid, Snackbar, Tooltip, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';

import { useReactHookForm } from "../../../../hooks/useReactHookForm";
import { useLoadData } from "../../../../hooks/useLoadData";
import { ApiResponse } from "../../../../models/api-response";
import { unitsApiKeyStore } from "../../../../api/units-api-key-store";
import { officeApiKeyStore } from "../../../../api/office-api-key-store";
import { projectsApiKeyStore } from "../../../../api/projects-api-key-store";
import { FormCheckbox } from "../../../../components/hook-form/form-checkbox.component";
import { ReactHookForm } from "../../../../components/hook-form/react-hook-form.component";
import { FormAutoCompleteSelectDynamic } from "../../../../components/hook-form/form-autocomplete-select.dynamic.component";
import { FormAutoCompleteMultiSelectDynamic } from "../../../query-builder/hooks-form-querybuilder/form-autocomplete-multi-select.dynamic.component";


const styling = {
    labelStyle: { paddingBottom: 5, fontWeight: 'bold', fontSize: 16 },
    moveCardStyle: {
        backgroundColor: '#EBEDEF',
        paddingBottom: 15,
        border: '1px solid #D5D8DC',
        borderRadius: 10
    },
};

interface MoveContactForm {
    customerID: number;
    officeCheckbox: boolean;
    projectCheckbox: boolean;
    unitCheckbox: boolean;
    handleOfficeCheckbox: (value: boolean) => void;
    handleProjectCheckbox: (value: boolean) => void;
    handleUnitCheckbox: (value: boolean) => void;

}

const MoveContactForm = ({
    customerID,
    officeCheckbox,
    projectCheckbox,
    unitCheckbox,
    handleOfficeCheckbox,
    handleProjectCheckbox,
    handleUnitCheckbox
}: MoveContactForm) => {
    const { setValue } = useFormContext();
    const [selectAll, setSelectAll] = useState(false);

    const project = useWatch({ name: 'project' });
    const projectID = project?.projectID ?? 0;

    const unitsQuery = unitsApiKeyStore.getProjectUnits(projectID);
    const { data: unitsData } = useLoadData<ApiResponse<any[]>>(unitsQuery, !!projectID);
    const allUnits = unitsData?.data ?? [];

    const handleOfficeChange = (value: any) => {
        setValue('isMapOffice', false)
        setValue('project', null)
        setValue('isMapProject', false)
        setValue('unit', [])
        setValue('isMapUnit', false)
        setSelectAll(false);
        handleOfficeCheckbox(value ? false : true);
        handleProjectCheckbox(true);
        handleUnitCheckbox(true);
    }

    const handleProjectChange = (value: any) => {
        setValue('isMapProject', false)
        setValue('unit', [])
        setValue('isMapUnit', false)
        setSelectAll(false);
        handleProjectCheckbox(value ? false : true);
        handleUnitCheckbox(true);
    }

    const handleUnitChange = (value: any) => {
        setValue('isMapUnit', false)
        handleUnitCheckbox(value?.length > 0 ? false : true);
        if (!value?.length || value.length < allUnits.length) {
            setSelectAll(false);
        }
    }

    const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setSelectAll(checked);
        if (checked) {
            setValue('unit', allUnits);
            setValue('isMapUnit', false);
            handleUnitCheckbox(allUnits.length > 0 ? false : true);
        } else {
            setValue('unit', []);
            setValue('isMapUnit', false);
            handleUnitCheckbox(true);
        }
    };

    return (
        <>
            <Grid item xl={3.5} lg={3.5} sm={3.5} xs={3.5}>
                <FormAutoCompleteSelectDynamic
                    name="office"
                    label="Office"
                    onChange={handleOfficeChange}
                    getQueryKeyConfig={() => officeApiKeyStore.getCustomerOffices(customerID ?? 0)}
                    optionsValueKey="officeID"
                    optionsLabelKey="officeName"
                />
                <FormCheckbox label="Map Office" name="isMapOffice" disabled={officeCheckbox} />
            </Grid>
            <Grid item xl={3.5} lg={3.5} sm={3.5} xs={3.5}>
                <FormAutoCompleteSelectDynamic
                    name="project"
                    label="Project"
                    onChange={handleProjectChange}
                    watchKeys={['office']}
                    getQueryKeyConfig={(value: any) => projectsApiKeyStore.getOfficeProjects(value.office?.officeID ?? 0)}
                    optionsValueKey="projectID"
                    optionsLabelKey="projectName"
                />
                <FormCheckbox label="Map Project" name="isMapProject" disabled={projectCheckbox} />
            </Grid>
            <Grid item xl={3.5} lg={3.5} sm={3.5} xs={3.5}>
                <FormAutoCompleteMultiSelectDynamic
                    name="unit"
                    label="Units"
                    watchKeys={['project']}
                    onChange={handleUnitChange}
                    getQueryKeyConfig={(value: any) => unitsApiKeyStore.getProjectUnits(value.project?.projectID ?? 0)}
                    optionsLabelKey="unitName"
                    optionsValueKey="unitID"
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FormCheckbox label="Map Unit" name="isMapUnit" disabled={unitCheckbox} />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectAll}
                                onChange={handleSelectAllChange}
                                disabled={!projectID || allUnits.length === 0}
                                size="small"
                            />
                        }
                        label="Select All"
                    />
                </div>
            </Grid>
        </>
    );
}

interface MoveContactComponent {
    customerID?: any;
    handleNewMappedData: (data: any) => void;
}

export const MoveContactComponent = ({ customerID, handleNewMappedData }: MoveContactComponent) => {
    const form = useReactHookForm({
        defaultValues: {
            office: { officeID: '', officeName: '' },
            project: { projectID: '', projectName: '' },
            unit: [],
            isMapOffice: false,
            isMapProject: false,
            isMapUnit: false
        }
    });

    const [isOpen, setIsOpen] = useState(false);
    const [officeCheckbox, setOfficeCheckbox] = useState<boolean>(true);
    const [projectCheckbox, setProjectCheckbox] = useState<boolean>(true);
    const [unitCheckbox, setUnitCheckbox] = useState<boolean>(true);
    const [message, setMessage] = useState({ severity: 'success', messageInfo: '' });

    useEffect(() => {
        if (customerID) {
            form.reset({
                office: { officeID: '', officeName: '' },
                project: { projectID: '', projectName: '' },
                unit: { unitID: '', unitName: '' },
                isMapOffice: false,
                isMapProject: false,
                isMapUnit: false
            })
            setOfficeCheckbox(true);
            setProjectCheckbox(true);
            setUnitCheckbox(true);
        }
    }, [customerID])

    const transformData = (data: any, movingData: any) => {
        return data.map((unit: any) => ({
            unitName: unit.unitName,
            unitNumber: unit.unitNumber,
            unitID: unit.unitID,
            unitMapping: movingData?.isMapUnit ?? false,
            isAdded: movingData?.isMapUnit ?? false,
        }));
    };

    const handleSubmit = async (movingData: any) => {
        if (!movingData?.isMapOffice && !movingData?.isMapProject && !movingData?.isMapUnit) {
            setIsOpen(true);
            setMessage({ severity: 'error', messageInfo: 'Please select atleast one mapped checkbox' });
            return;
        }
        const mappingData = {
            contactsOfficeMappings: [
                {
                    officeCode: movingData?.office?.officeCode,
                    officeName: movingData?.office?.officeName,
                    officeID: movingData?.office?.officeID,
                    officeMapping: movingData?.isMapOffice ?? false,
                    projectCount: 1,
                    unitCount: 1,
                    isAdded: movingData?.isMapOffice ?? false,
                    contactsProjectMappings: movingData?.project?.projectID ? [
                        {
                            projectName: movingData?.project?.projectName,
                            projectCode: movingData?.project?.projectCode,
                            projectID: movingData?.project?.projectID,
                            projectMapping: movingData?.isMapProject ?? false,
                            unitCount: 1,
                            isAdded: movingData?.isMapProject ?? false,
                            contactsUnitMappings: movingData?.unit?.length > 0 ?
                                transformData(movingData?.unit, movingData)
                                : []
                        }
                    ] : []
                }
            ]
        }

        handleNewMappedData(mappingData);
        // setOfficeCheckbox(true);
        // setProjectCheckbox(true);
        // setUnitCheckbox(true);
        // form.reset({
        //     office: { officeID: '', officeName: '' },
        //     project: { projectID: '', projectName: '' },
        //     unit: { unitID: '', unitName: '' },
        //     isMapOffice: false,
        //     isMapProject: false,
        //     isMapUnit: false
        // })
    };


    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setMessage({ severity: 'success', messageInfo: '' });
        setIsOpen(false);
    };

    const handleOfficeCheckbox = (value: boolean) => { setOfficeCheckbox(value); }
    const handleProjectCheckbox = (value: boolean) => { setProjectCheckbox(value); }
    const handleUnitCheckbox = (value: boolean) => { setUnitCheckbox(value); }


    const handleSubmitClick = () => {
        form.handleSubmit(handleSubmit)();
    };

    return (
        <ReactHookForm formName="ADD_MODULES_CONTACT_FORM" onSubmit={handleSubmit} formContextProps={form}>
            <Card elevation={0}>
                <CardHeader style={{ padding: 0 }}
                    title={
                        <Typography variant="h6" style={styling.labelStyle}>
                            Map / Move to Office / Project / Unit
                        </Typography>
                    }
                />
                <CardContent style={styling.moveCardStyle}>
                    <Grid container spacing={2}>
                        <MoveContactForm
                            customerID={customerID}
                            officeCheckbox={officeCheckbox}
                            projectCheckbox={projectCheckbox}
                            unitCheckbox={unitCheckbox}
                            handleOfficeCheckbox={handleOfficeCheckbox}
                            handleProjectCheckbox={handleProjectCheckbox}
                            handleUnitCheckbox={handleUnitCheckbox}
                        />
                        <Grid item xl={1} lg={1} sm={1} xs={1}>
                            <Tooltip title={<Typography variant="body1">Map</Typography>}>
                                <Button
                                    fullWidth
                                    type="button"
                                    variant="contained"
                                    disabled={officeCheckbox}
                                    onClick={handleSubmitClick}
                                    style={{ height: 38, maxWidth: 38 }}>
                                    <AddIcon />
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <Snackbar
                open={isOpen}
                autoHideDuration={6000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                onClose={handleClose}
            >
                <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={handleClose}>
                    {!message.messageInfo ? ' ' : message?.messageInfo}
                </Alert>
            </Snackbar>
        </ReactHookForm>
    );
}